import type { SupWeb3WithdrawSingleVault } from "@/types";
import type { Web3WithdrawAmountProps } from "@/components/Web3/Withdraw/Amount/Wrapper";
import React, { Fragment, useCallback, useMemo } from "react";

import config from "@/../config/dapp.config";
import { SupSlider, SupText } from "@repo/core-ui";
import { FixedNumber } from "@ethersproject/bignumber";
import { formatCurrencyCompact } from "@/utils";
import { twMerge } from "tailwind-merge";

export default function Web3WithdrawAmountSlider({ setVaultList, selectedBalance, singleVault }: Web3WithdrawAmountProps) {
  const handlePercentChange = useCallback(
    (value: number | number[]) => {
      const percent = FixedNumber.from(`${(value as number) / 100}`);
      setVaultList(currentVaultList =>
        currentVaultList.map(vault => {
          return vault.id === singleVault.id
            ? ({
                ...vault,
                amount: vault.balance?.mulUnsafe(FixedNumber.from(`${percent}`)) || FixedNumber.from("0"),
                amountInFiat: vault.balanceInFiat?.mulUnsafe(FixedNumber.from(`${percent}`)) || FixedNumber.from("0"),
              } as SupWeb3WithdrawSingleVault)
            : vault;
        })
      );
    },
    [setVaultList, singleVault.id]
  );

  const { value, totalFiatAmount } = useMemo(() => {
    const totalFiatAmount = singleVault.amountInFiat?.toUnsafeFloat() ?? 0;
    const totalAmount = singleVault.amount ?? FixedNumber.from("0");
    const value =
      totalFiatAmount === 0
        ? totalAmount
            .divUnsafe(singleVault.balance ?? FixedNumber.from("1"))
            .mulUnsafe(FixedNumber.from("100"))
            .toUnsafeFloat()
        : (totalFiatAmount / (singleVault.balanceInFiat?.toUnsafeFloat() || 1)) * 100;
    return {
      value,
      totalFiatAmount,
      totalAmount,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleVault.amount, singleVault.amountInFiat, singleVault.balance, singleVault.balanceInFiat, selectedBalance]);

  return (
    <Fragment>
      <div className="flex h-[52px] w-full flex-col items-start justify-start gap-1 self-stretch">
        <SupSlider
          grabberType="value"
          className="w-full"
          value={value}
          marks={50}
          formatDisplayValue={value => (typeof value === "undefined" ? undefined : `${value}%`)}
          onAfterChange={handlePercentChange}
          pushable
        />
      </div>
      <div className="inline-flex h-4 w-full items-center justify-between gap-1">
        <div className={twMerge("flex h-4 shrink grow basis-0 items-center justify-end gap-1", totalFiatAmount === 0 && "invisible")}>
          <SupText variant="caption" className="leading-[16px]">
            WITHDRAWING TODAY
          </SupText>
          <SupText variant="button" weight="bold">
            {formatCurrencyCompact(totalFiatAmount, config.decimalsRoundingLimit, config.decimalsRoundingLimit, "currency", "standard")}
          </SupText>
        </div>
      </div>
    </Fragment>
  );
}
